import type { CoworldReplayTelemetryContext } from "../replay-telemetry";

export type ReplayOutcome =
  | "success"
  | "session_error"
  | "runtime_timeout"
  | "iframe_error"
  | "viewer_error"
  | "viewer_timeout";
export type ReplayTiming = {
  requestStartedAt: number;
  sessionShared: boolean;
  responseReceivedAt: number | null;
  runtimeReadyAt: number | null;
  iframeInsertedAt: number | null;
  iframeLoadedAt: number | null;
  bundleReadyAt: number | null;
  replayFetchStartAt: number | null;
  replayFetchEndAt: number | null;
  replayParsedAt: number | null;
  replayBytes: number | undefined;
  replayCompressed: boolean | undefined;
  viewerManifestHash: string | null;
  hiddenMs: number;
  hiddenSince: number | null;
  healthPollCount: number;
  delivery: "static_bundle" | "static" | "runtime" | null;
};
export type ReplayIdentity = Readonly<{
  coworldId: string;
  telemetryContext: CoworldReplayTelemetryContext;
  surface: string;
}>;

export function freshReplayTiming({
  requestStartedAt,
  hiddenSince,
  sessionShared,
}: {
  requestStartedAt: number;
  hiddenSince: number | null;
  sessionShared: boolean;
}): ReplayTiming {
  return {
    requestStartedAt,
    sessionShared,
    responseReceivedAt: null,
    runtimeReadyAt: null,
    iframeInsertedAt: null,
    iframeLoadedAt: null,
    bundleReadyAt: null,
    replayFetchStartAt: null,
    replayFetchEndAt: null,
    replayParsedAt: null,
    replayBytes: undefined,
    replayCompressed: undefined,
    viewerManifestHash: null,
    hiddenMs: 0,
    hiddenSince,
    healthPollCount: 0,
    delivery: null,
  };
}

export function durationMs({
  start,
  end,
}: Readonly<{
  start: number | null;
  end: number | null;
}>): number | undefined {
  return start === null || end === null ? undefined : Math.round(end - start);
}

export const replayTerminals = {
  sessionError: { outcome: "session_error", failureStage: "session_create" },
  runtimeError: { outcome: "session_error", failureStage: "runtime_ready" },
  runtimeTimeout: { outcome: "runtime_timeout", failureStage: "runtime_ready" },
  iframeReady: { outcome: "success", failureStage: null },
  iframeError: {
    outcome: "iframe_error",
    failureStage: "iframe_document_load",
  },
  viewerReady: {
    outcome: "success",
    failureStage: null,
    measurementKind: "viewer_ready_message",
  },
  viewerError: {
    outcome: "viewer_error",
    failureStage: "viewer_replay_load",
    measurementKind: "viewer_ready_message",
  },
  viewerTimeout: {
    outcome: "viewer_timeout",
    failureStage: "viewer_ready",
    measurementKind: "viewer_ready_message",
  },
} as const;
